import { useState, useEffect } from 'react';
import api from '../../services/api';
import SearchableSelect from '../Common/SearchableSelect';
import InstrumentPicker from '../Common/InstrumentPicker';
import { useAuth } from '../../context/AuthContext';

const INPUT = 'w-full px-3 py-2 bg-dark-700 border border-dark-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';
const LABEL = 'block text-xs font-medium text-dark-300 mb-1';

export default function EditInstrumentModal({ instrument, onClose, onUpdated }) {
  const { user } = useAuth();

  // Identificação
  const [tag, setTag] = useState(instrument?.tag || '');
  const [description, setDescription] = useState(instrument?.description || '');
  const [sector, setSector] = useState(instrument?.sector || '');
  const [instrumentClassId, setInstrumentClassId] = useState(instrument?.instrumentClassId || '');
  const [tipoInstrumento, setTipoInstrumento] = useState(instrument?.tipoInstrumento || '');
  const [localCalibracao, setLocalCalibracao] = useState(instrument?.localCalibracao || '');
  const [operationalStatus, setOperationalStatus] = useState(instrument?.operationalStatus || 'ativo');
  const [criticity, setCriticity] = useState(instrument?.criticity || 'medium');
  const [calibratable, setCalibratable] = useState(instrument?.calibratable ?? true);
  const [calibrationFrequencyDays, setCalibrationFrequencyDays] = useState(instrument?.calibrationFrequencyDays || '');

  // Identificação técnica
  const [marca, setMarca] = useState(instrument?.marca || '');
  const [modelo, setModelo] = useState(instrument?.modelo || '');
  const [numeroDeSerie, setNumeroDeSerie] = useState(instrument?.numeroDeSerie || '');
  const [partNumber, setPartNumber] = useState(instrument?.partNumber || '');

  // Metrologia
  const [resolucao, setResolucao] = useState(instrument?.resolucao || '');
  const [exatidao, setExatidao] = useState(instrument?.exatidao || '');
  const [tipoFaixa, setTipoFaixa] = useState(instrument?.tipoFaixa || '');
  const [modoComparacao, setModoComparacao] = useState(instrument?.modoComparacao || '');
  const [codigoEquipamentoSuperior, setCodigoEquipamentoSuperior] = useState(instrument?.codigoEquipamentoSuperior || '');

  // Faixas
  const [usageMin, setUsageMin] = useState(instrument?.usageRange?.min ?? '');
  const [usageMax, setUsageMax] = useState(instrument?.usageRange?.max ?? '');
  const [usageUnit, setUsageUnit] = useState(instrument?.usageRange?.unit || '');
  const [nominalMin, setNominalMin] = useState(instrument?.nominalRange?.min ?? '');
  const [nominalMax, setNominalMax] = useState(instrument?.nominalRange?.max ?? '');
  const [nominalUnit, setNominalUnit] = useState(instrument?.nominalRange?.unit || '');

  const [sectors, setSectors] = useState([]);
  const [classes, setClasses] = useState([]);
  const [freqFromClass, setFreqFromClass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadSectors(); loadClasses(); }, []);

  const loadSectors = async () => {
    try {
      const res = await api.get('/instruments/sectors');
      setSectors(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreateSector = async (name) => {
    await api.post('/instruments/sectors', { name });
    await loadSectors();
  };

  const loadClasses = async () => {
    try {
      const res = await api.get('/instrument-classes');
      setClasses(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const updates = {
        description, sector, operationalStatus, criticity, calibratable,
        instrumentClassId: instrumentClassId || null,
        tipoInstrumento: tipoInstrumento || null,
        localCalibracao: localCalibracao || null,
        calibrationFrequencyDays: calibratable && calibrationFrequencyDays ? parseInt(calibrationFrequencyDays) : null,
        marca: marca || null, modelo: modelo || null,
        numeroDeSerie: numeroDeSerie || null, partNumber: partNumber || null,
        ...(calibratable && {
          resolucao: resolucao || null, exatidao: exatidao || null,
          tipoFaixa: tipoFaixa || null, modoComparacao: modoComparacao || null,
          codigoEquipamentoSuperior: codigoEquipamentoSuperior || null,
          usageRange: usageMin || usageMax ? { min: parseFloat(usageMin) || 0, max: parseFloat(usageMax) || 0, unit: usageUnit } : undefined,
          nominalRange: nominalMin || nominalMax ? { min: parseFloat(nominalMin) || 0, max: parseFloat(nominalMax) || 0, unit: nominalUnit } : undefined,
        }),
      };
      if (user?.role === 'supervisor' && tag !== instrument.tag) updates.tag = tag;

      const res = await api.patch(`/instruments/${instrument._id}`, updates);
      if (onUpdated) onUpdated(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 border border-dark-600 rounded-xl w-full max-w-2xl text-white flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center p-6 border-b border-dark-600">
          <h2 className="text-lg font-bold">Editar Instrumento — {instrument?.tag}</h2>
          <button type="button" onClick={onClose} className="text-dark-400 hover:text-white">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-5">
          {/* Identificação */}
          <div>
            <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Identificação</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className={LABEL}>TAG</label>
                <input type="text" value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  disabled={user?.role !== 'supervisor'}
                  className={`${INPUT} disabled:bg-dark-600 disabled:text-dark-400`} required />
              </div>
              <div>
                <label className={LABEL}>Classe</label>
                <select
                  value={instrumentClassId}
                  onChange={e => {
                    const id = e.target.value;
                    setInstrumentClassId(id);
                    const cls = classes.find(c => c._id === id);
                    if (cls?.calibrationFrequencyDays && !calibrationFrequencyDays) {
                      setCalibrationFrequencyDays(String(cls.calibrationFrequencyDays));
                      setFreqFromClass(true);
                    } else if (!id && freqFromClass) {
                      setCalibrationFrequencyDays('');
                      setFreqFromClass(false);
                    }
                  }}
                  className={INPUT}
                >
                  <option value="">— Sem classe —</option>
                  {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="mb-3">
              <label className={LABEL}>Descrição *</label>
              <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className={INPUT} required />
            </div>
            <div className="mb-3">
              <label className={LABEL}>Setor</label>
              <SearchableSelect options={sectors} value={sector} onChange={setSector} placeholder="Digite para buscar..." onCreateOption={handleCreateSector} />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className={LABEL}>Status Operacional</label>
                <select value={operationalStatus} onChange={(e) => setOperationalStatus(e.target.value)} className={INPUT}>
                  <option value="ativo">Ativo</option>
                  <option value="desativado">Desativado</option>
                  <option value="backup">Backup</option>
                  <option value="manutencao">Manutenção</option>
                </select>
              </div>
              <div>
                <label className={LABEL}>Criticidade</label>
                <select value={criticity} onChange={(e) => setCriticity(e.target.value)} className={INPUT}>
                  <option value="high">Alta</option>
                  <option value="medium">Média</option>
                  <option value="low">Baixa</option>
                </select>
              </div>
              <div className="flex flex-col justify-center">
                <label className={LABEL}>Calibrável</label>
                <button
                  type="button"
                  onClick={() => setCalibratable(v => !v)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    calibratable
                      ? 'bg-emerald-900/30 border-emerald-700/50 text-emerald-400'
                      : 'bg-dark-700 border-dark-500 text-dark-400'
                  }`}
                >
                  <span className={`w-3 h-3 rounded-full ${calibratable ? 'bg-emerald-400' : 'bg-dark-400'}`} />
                  {calibratable ? 'Sim' : 'Não'}
                </button>
              </div>
            </div>
            {calibratable && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className={LABEL} style={{ margin: 0 }}>Freq. Calibração (dias)</label>
                  {freqFromClass && (
                    <span className="text-[10px] text-blue-400 bg-blue-950/30 border border-blue-800/40 px-2 py-0.5 rounded-full">
                      herdado da classe
                    </span>
                  )}
                </div>
                <input
                  type="number" min="1" value={calibrationFrequencyDays}
                  onChange={e => { setCalibrationFrequencyDays(e.target.value); setFreqFromClass(false); }}
                  placeholder="365" className={INPUT}
                />
                {(() => {
                  const cls = classes.find(c => c._id === instrumentClassId);
                  return cls?.criterioAceitacao ? (
                    <p className="text-[10px] text-emerald-400 mt-1">✓ Critério da classe: {cls.criterioAceitacao}</p>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          {/* Identificação Técnica */}
          <div>
            <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Identificação Técnica</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className={LABEL}>Marca / Fabricante</label>
                <input type="text" value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="Ex: Fluke" className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Modelo</label>
                <input type="text" value={modelo} onChange={(e) => setModelo(e.target.value)} className={INPUT} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Número de Série</label>
                <input type="text" value={numeroDeSerie} onChange={(e) => setNumeroDeSerie(e.target.value)} className={INPUT} />
              </div>
              <div>
                <label className={LABEL}>Part Number</label>
                <input type="text" value={partNumber} onChange={(e) => setPartNumber(e.target.value)} className={INPUT} />
              </div>
            </div>
          </div>

          {calibratable && (
            <>
              {/* Características Metrológicas */}
              <div>
                <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Características Metrológicas</p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className={LABEL}>Resolução</label>
                    <input type="text" value={resolucao} onChange={(e) => setResolucao(e.target.value)} placeholder="Ex: 0,01 °C" className={INPUT} />
                  </div>
                  <div>
                    <label className={LABEL}>Exatidão / Classe</label>
                    <input type="text" value={exatidao} onChange={(e) => setExatidao(e.target.value)} placeholder="Ex: ±0,5%" className={INPUT} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className={LABEL}>Tipo de Instrumento</label>
                    <select value={tipoInstrumento} onChange={(e) => setTipoInstrumento(e.target.value)} className={INPUT}>
                      <option value="">— selecione —</option>
                      <option value="portatil">Portátil</option>
                      <option value="fixo">Fixo</option>
                      <option value="componente_malha">Componente de Malha</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Local de Calibração</label>
                    <select value={localCalibracao} onChange={(e) => setLocalCalibracao(e.target.value)} className={INPUT}>
                      <option value="">— selecione —</option>
                      <option value="in_loco">In loco (lab vem ao local)</option>
                      <option value="laboratorio">Laboratório (equipamento enviado)</option>
                      <option value="ambos">Ambos</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Tipo de Faixa</label>
                    <select value={tipoFaixa} onChange={(e) => setTipoFaixa(e.target.value)} className={INPUT}>
                      <option value="">— selecione —</option>
                      <option value="linear">Linear</option>
                      <option value="por_relacao">Por Relação</option>
                      <option value="inversa">Inversa</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Modo de Comparação</label>
                    <select value={modoComparacao} onChange={(e) => setModoComparacao(e.target.value)} className={INPUT}>
                      <option value="">— selecione —</option>
                      <option value="fixo">Fixo</option>
                      <option value="variavel">Variável</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Instrumento Pai (TAG)</label>
                  <InstrumentPicker
                    value={codigoEquipamentoSuperior}
                    onChange={setCodigoEquipamentoSuperior}
                    excludeTag={tag}
                    placeholder="Digite a TAG para buscar..."
                  />
                </div>
              </div>

              {/* Faixas */}
              <div>
                <p className="text-xs font-semibold text-dark-400 uppercase tracking-wider mb-3">Faixas de Operação</p>
                <div className="mb-3">
                  <p className="text-xs text-dark-400 mb-2">Faixa de Uso</p>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" step="any" placeholder="Min" value={usageMin} onChange={e => setUsageMin(e.target.value)} className={INPUT} />
                    <input type="number" step="any" placeholder="Max" value={usageMax} onChange={e => setUsageMax(e.target.value)} className={INPUT} />
                    <input type="text" placeholder="Unidade" value={usageUnit} onChange={e => setUsageUnit(e.target.value)} className={INPUT} />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-dark-400 mb-2">Faixa Nominal</p>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" step="any" placeholder="Min" value={nominalMin} onChange={e => setNominalMin(e.target.value)} className={INPUT} />
                    <input type="number" step="any" placeholder="Max" value={nominalMax} onChange={e => setNominalMax(e.target.value)} className={INPUT} />
                    <input type="text" placeholder="Unidade" value={nominalUnit} onChange={e => setNominalUnit(e.target.value)} className={INPUT} />
                  </div>
                </div>
              </div>
            </>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-2 justify-end pt-2 border-t border-dark-600">
            <button type="button" onClick={onClose} className="px-4 py-2 text-dark-300 border border-dark-500 rounded-lg hover:border-dark-400 text-sm">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
